import React from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  IconButton,
  Divider,
  Flex,
  CloseButton,
  Badge,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';
import { FaVolumeUp, FaSave, FaCheck } from 'react-icons/fa';
import { ArticleWordTranslation } from '../../services/smartReadApi';
import { useTranslation } from 'react-i18next';

interface WordPopupProps {
  word: string;
  data: ArticleWordTranslation;
  onPlayPronunciation: () => void;
  onSaveWord: () => void;
  onMarkKnown: () => void;
  onClose: () => void;
  isPlayingWord?: boolean;
  userLanguage: string;
}

const MotionBox = motion(Box);

export function WordPopup({
  word,
  data,
  onPlayPronunciation,
  onSaveWord,
  onMarkKnown,
  onClose,
  isPlayingWord = false,
  userLanguage,
}: WordPopupProps) {
  const { t } = useTranslation();

  // Color mode values
  const popupBg = useColorModeValue('white', 'gray.800');
  const popupBorder = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const dividerColor = useColorModeValue('gray.100', 'gray.700');

  const buttonPlayBg = useColorModeValue('blue.50', 'blue.800');
  const buttonPlayColor = useColorModeValue('blue.600', 'blue.200');
  const buttonPlayHoverBg = useColorModeValue('blue.100', 'blue.700');

  const buttonSaveBg = useColorModeValue('green.50', 'green.800');
  const buttonSaveColor = useColorModeValue('green.600', 'green.200');
  const buttonSaveHoverBg = useColorModeValue('green.100', 'green.700');

  const buttonKnownBg = useColorModeValue('purple.50', 'purple.800');
  const buttonKnownColor = useColorModeValue('purple.600', 'purple.200');
  const buttonKnownHoverBg = useColorModeValue('purple.100', 'purple.700');

  if (!data) return null;

  // Get translations based on available data
  const getTranslations = () => {
    const translations = [];

    if (data?.en) {
      translations.push({ language: 'English', text: data.en, code: 'en' });
    }

    if (data?.he) {
      translations.push({ language: 'Hebrew', text: data.he, code: 'he' });
    }

    if (data?.nl) {
      translations.push({ language: 'Dutch', text: data.nl, code: 'nl' });
    }

    return translations;
  };

  // Determine the source language
  const sourceLanguage = data.article_language || 'unknown';

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      position="fixed"
      zIndex={100}
      width="280px"
      bg={popupBg}
      borderRadius="md"
      boxShadow="lg"
      borderWidth="1px"
      borderColor={popupBorder}
      p={4}
      bottom="20px"
      left="50%"
      style={{ x: "-50%" }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Flex justify="space-between" align="center" mb={3}>
        <HStack>
          <Heading size="md" color={headingColor}>
            {word || data.word || 'Unknown Word'}
          </Heading>
          <Badge colorScheme="purple" fontSize="xs">
            {sourceLanguage.toUpperCase()}
          </Badge>
        </HStack>
        <CloseButton size="sm" onClick={onClose} />
      </Flex>

      <Divider mb={3} borderColor={dividerColor} />

      <VStack align="stretch" spacing={2} mb={4}>
        {getTranslations().length > 0 ? (
          getTranslations().map((translation, index) => (
            <Box key={index}>
              <HStack>
                <Text fontSize="xs" fontWeight="medium" color={labelColor} width="60px">
                  {translation.language}:
                </Text>
                <Text fontWeight={userLanguage && translation.code === userLanguage ? "semibold" : "normal"}>
                  {translation.text}
                </Text>
              </HStack>
            </Box>
          ))
        ) : (
          <Box>
            <Text color={labelColor} fontStyle="italic">
                {t('smartRead.wordPopup.noTranslations')}
            </Text>
          </Box>
        )}
      </VStack>

      <HStack spacing={2} justify="center">
        <Tooltip label={t('smartRead.wordPopup.playPronunciation')} hasArrow>
          <IconButton
            icon={<FaVolumeUp />}
            onClick={onPlayPronunciation}
            isDisabled={isPlayingWord}
            aria-label={t('smartRead.wordPopup.playPronunciation')}
            isLoading={isPlayingWord}
            size="sm"
            bg={buttonPlayBg}
            color={buttonPlayColor}
            _hover={{ bg: buttonPlayHoverBg }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            borderRadius="full"
          />
        </Tooltip>

        <Tooltip label={t('smartRead.wordPopup.saveToCollection')} hasArrow>
          <IconButton
            icon={<FaSave />}
            onClick={onSaveWord}
            aria-label={t('smartRead.wordPopup.saveToCollection')}
            size="sm"
            bg={buttonSaveBg}
            color={buttonSaveColor}
            _hover={{ bg: buttonSaveHoverBg }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            borderRadius="full"
          />
        </Tooltip>

        <Tooltip label={t('smartRead.wordPopup.markAsKnown')} hasArrow>
          <IconButton
            icon={<FaCheck />}
            onClick={onMarkKnown}
            aria-label={t('smartRead.wordPopup.markAsKnown')}
            size="sm"
            bg={buttonKnownBg}
            color={buttonKnownColor}
            _hover={{ bg: buttonKnownHoverBg }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            borderRadius="full"
          />
        </Tooltip>
      </HStack>
    </MotionBox>
  );
}
